import {
  anyCableLinkSchema,
  cableLinkSchema,
  cableLinkWarningSchema,
  cableLinksSchema,
  trayCableLinkSchema,
  type AnyCableLink,
  type CableLink,
  type CableLinks,
  type TrayCableLink,
} from "../services/calculationRun";
import { formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";

// The cable runs a study read its rows from, pinned per row in the run's references snapshot as
// `cable_links` keyed by the row code: an earth-fault loop study's conductor figures (EOS-05d,
// A33, D5) or a cable tray study's installation facts (EOS-12, A34) - the run and its revision,
// the facts as read with the exact decimals in the tooltips (A12), the run's own status, and a
// REVIEW outcome with one card per warning. Nothing is shown for a study whose rows named no
// cable run (the panel is not rendered at all).

// The schemas live in the shared run contract (services/calculationRun.ts), beside the
// fault and load links the report document carries; re-exported for the panel's readers.
export { anyCableLinkSchema, cableLinkSchema, cableLinkWarningSchema, cableLinksSchema, trayCableLinkSchema };
export type { AnyCableLink, CableLink, CableLinks, TrayCableLink };

export const CABLE_LINK_CODE_LABELS: Record<string, string> = {
  CABLE_RUN_NOT_VALID: "Cable run not a passed design check",
  PROTECTIVE_CONDUCTOR_NOT_ESTABLISHED: "Protective conductor not established",
  INSTALLATION_METHOD_MISMATCH: "Installation method differs from the tray arrangement",
  GROUPING_DIFFERS: "Grouping differs from the route",
  INSTALLATION_METHOD_TRAY_GENERIC: "Generic tray method (arrangement not compared)",
  INSTALLATION_METHOD_ENGINEERED: "Engineered installation basis (not compared)",
};

/** A cable tray pin carries the installation method; a conductor-figures pin never does. */
export function isTrayLink(link: AnyCableLink): link is TrayCableLink {
  return "installation_method" in link;
}

/** The `cable_links` of a run's references snapshot, or null when absent or unreadable. */
export function cableLinksOf(references: Record<string, unknown> | null | undefined): CableLinks | null {
  if (!references) {
    return null;
  }
  const parsed = cableLinksSchema.safeParse(references.cable_links);
  return parsed.success && Object.keys(parsed.data).length > 0 ? parsed.data : null;
}

/** "multicore Cu XLPE 70 mm² · 3 runs · E_FREE_AIR_MULTICORE · 3 grouped circuits" */
export function trayLinkDetailText(link: TrayCableLink): string {
  const material = link.conductor_material === "COPPER" ? "Cu" : link.conductor_material === "ALUMINIUM" ? "Al" : link.conductor_material;
  const area = link.phase_area_mm2 === null ? "" : ` ${formatQuantity(link.phase_area_mm2, "mm²").display}`;
  const runs = `${link.parallel_runs} ${link.parallel_runs === 1 ? "run" : "runs"}`;
  const grouped =
    link.grouped_circuits === null
      ? "grouping not recorded"
      : `${link.grouped_circuits} grouped ${link.grouped_circuits === 1 ? "circuit" : "circuits"}`;
  return `${link.construction.toLowerCase().replace("_", "-")} ${material} ${link.insulation_material}${area} · ${runs} · ${link.installation_method} · ${grouped}`;
}

/** "70 mm² / 35 mm² Cu · XLPE 90 °C · 120 m · 1 run · x 0.08 Ω/km" */
export function cableLinkDetailText(link: CableLink): string {
  const phase = formatQuantity(link.phase_area_mm2, "mm²").display;
  const protective =
    link.protective_area_mm2 === null ? "no PE area" : formatQuantity(link.protective_area_mm2, "mm²").display;
  const material = link.conductor_material === "COPPER" ? "Cu" : link.conductor_material === "ALUMINIUM" ? "Al" : link.conductor_material;
  const temperature = formatQuantity(link.insulation_maximum_temperature_c, "°C").display;
  const length = formatQuantity(link.route_length_m, "m").display;
  const runs = `${link.parallel_runs} ${link.parallel_runs === 1 ? "run" : "runs"}`;
  const reactance =
    link.reactance_ohm_per_km === null ? "no reactance" : `x ${formatQuantity(link.reactance_ohm_per_km, "Ω/km").display}`;
  return `${phase} / ${protective} ${material} · ${link.insulation_material} ${temperature} · ${length} · ${runs} · ${reactance}`;
}

type CableLinksPanelProps = {
  links: CableLinks | null | undefined;
};

export function CableLinksPanel({ links }: CableLinksPanelProps) {
  if (!links) {
    return null;
  }
  const entries = Object.entries(links);
  const allValid = entries.every(([, link]) => link.status === "VALID");
  const tray = entries.some(([, link]) => isTrayLink(link));
  const what = tray ? "Installation facts" : "Conductor figures";
  const rowWord = tray ? "Row" : "Circuit";
  return (
    <section aria-label="Cable runs linked" data-summary-tone={allValid ? "pass" : "warning"}>
      <header>
        <p data-summary-status={allValid ? "VALID" : "REVIEW_REQUIRED"}>
          <StatusIcon tone={allValid ? "pass" : "review"} size={20} />
          {allValid
            ? `${what} from ${entries.length} linked cable ${entries.length === 1 ? "run" : "runs"}: valid`
            : `${what} from ${entries.length} linked cable ${entries.length === 1 ? "run" : "runs"}: review required`}
        </p>
      </header>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">{rowWord}</th>
              <th scope="col">Cable run</th>
              <th scope="col">{tray ? "Installation as read" : "Figures as read"}</th>
              <th scope="col">Run status</th>
              <th scope="col">Link</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([code, link]) => (
              <tr key={code} data-cable-link={code} data-cable-link-status={link.status}>
                <th scope="row">{code}</th>
                <td title={link.content_hash}>
                  {link.calculation_key} rev {link.revision_number}
                </td>
                {isTrayLink(link) ? (
                  <td title={`${link.phase_area_mm2 ?? "—"} mm² · ${link.installation_method}`}>
                    {trayLinkDetailText(link)}
                  </td>
                ) : (
                  <td title={`${link.phase_area_mm2} / ${link.protective_area_mm2 ?? "—"} mm² · ${link.route_length_m} m`}>
                    {cableLinkDetailText(link)}
                  </td>
                )}
                <td data-cable-run-status={link.run_status}>{link.run_status.replaceAll("_", " ").toLowerCase()}</td>
                <td>
                  {link.status !== "VALID"
                    ? `Review (${link.warnings.length})`
                    : link.warnings.length === 0
                      ? "Valid"
                      : `Valid (${link.warnings.length} ${link.warnings.length === 1 ? "note" : "notes"})`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {entries.some(([, link]) => link.warnings.length > 0) ? (
        <ul data-warning-count={entries.reduce((count, [, link]) => count + link.warnings.length, 0)}>
          {entries.flatMap(([code, link]) =>
            link.warnings.map((warning, index) => (
              <li
                key={`${code}-${warning.code}-${index}`}
                data-warning-code={warning.code}
                data-warning-tone={link.status === "VALID" ? "not-evaluated" : "review"}
              >
                <strong>
                  <StatusIcon tone={link.status === "VALID" ? "not-evaluated" : "review"} size={16} />
                  {rowWord} {code}: {CABLE_LINK_CODE_LABELS[warning.code] ?? warning.code}
                </strong>
                {": "}
                <span>{warning.message}</span>
              </li>
            )),
          )}
        </ul>
      ) : null}
    </section>
  );
}

export default CableLinksPanel;
